import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc,
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp,
  onSnapshot,
  runTransaction,
  addDoc,
  updateDoc,
  writeBatch,
  deleteDoc,
  deleteField
} from 'firebase/firestore';
import { generateMockTransactions, initialAccounts, getMockTransactions, generateInvestmentReturnTransactions } from './mockData';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';

export async function initializeUserData(uid, email) {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    // Create user profile
    await setDoc(userRef, {
      email,
      name: email.split('@')[0],
      role: 'user',
      createdAt: serverTimestamp()
    });

    // Initialize accounts
    const accountsRef = collection(db, `users/${uid}/accounts`);
    for (const account of initialAccounts) {
      await setDoc(doc(accountsRef), {
        ...account,
        createdAt: serverTimestamp()
      });
    }

    // Initialize mock transactions
    const transactionsRef = collection(db, 'transactions');
    const mockTransactions = generateMockTransactions();
    
    for (const transaction of mockTransactions) {
      await setDoc(doc(transactionsRef), {
        ...transaction,
        userId: uid,
        createdAt: serverTimestamp(),
        date: transaction.date
      });
    }
  }
}

export async function getUserData(uid) {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) {
    throw new Error('User not found');
  }
  return userDoc.data();
}

function hasInvestmentAccount(accounts) {
  return Array.isArray(accounts) && accounts.some(
    (a) => String(a?.type || '').toLowerCase() === 'investment'
  );
}

export function isAccountDisabled(account) {
  return account?.disabled === true;
}

export function getActiveAccounts(accounts) {
  if (!Array.isArray(accounts)) return [];
  return accounts.filter((a) => !isAccountDisabled(a));
}

function getInvestmentAccount(accounts) {
  if (!Array.isArray(accounts)) return null;
  return (
    accounts.find(
      (a) =>
        String(a?.type || '').toLowerCase() === 'investment' && !isAccountDisabled(a)
    ) || null
  );
}

const INVESTMENT_RETURN_SOURCE = 'investment-returns';

async function deleteAutoGeneratedInvestmentTransactions(userId) {
  const transactionsRef = collection(db, 'transactions');
  const q = query(transactionsRef, where('userId', '==', userId));
  const querySnapshot = await getDocs(q);
  const batch = writeBatch(db);
  let deleteCount = 0;

  querySnapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (data?.source === INVESTMENT_RETURN_SOURCE || (data?.isAutoGenerated === true && data?.accountType === 'Investment')) {
      batch.delete(docSnap.ref);
      deleteCount += 1;
    }
  });

  if (deleteCount > 0) {
    await batch.commit();
  }

  return deleteCount;
}

export async function syncInvestmentReturnTransactions(userId, availableBalance) {
  const balance = Math.max(0, Number(availableBalance) || 0);
  await deleteAutoGeneratedInvestmentTransactions(userId);

  if (balance <= 0) {
    return 0;
  }

  const generated = generateInvestmentReturnTransactions(balance);
  if (generated.length === 0) {
    return 0;
  }

  const transactionsRef = collection(db, 'transactions');
  const batch = writeBatch(db);

  generated.forEach((transaction) => {
    const docRef = doc(transactionsRef);
    batch.set(docRef, {
      userId,
      accountType: 'Investment',
      type: transaction.type,
      amount: transaction.amount,
      description: transaction.description,
      date: transaction.date,
      category: transaction.category,
      status: transaction.status,
      source: INVESTMENT_RETURN_SOURCE,
      isAutoGenerated: true,
      createdAt: serverTimestamp(),
      createdBy: 'system',
    });
  });

  await batch.commit();
  return generated.length;
}

export async function getRecentTransactions(uid) {
  const transactionsRef = collection(db, 'transactions');
  const q = query(
    transactionsRef,
    where('userId', '==', uid),
    orderBy('date', 'desc'),
    limit(20)
  );
  const querySnapshot = await getDocs(q);
  const realTransactions = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    date: doc.data().date?.toDate?.() || doc.data().date
  }));

  const userDoc = await getDoc(doc(db, 'users', uid));
  const userData = userDoc.exists() ? userDoc.data() : {};
  const includeMock = userData.showMockTransactions === true;
  const allTransactions = includeMock
    ? [
        ...realTransactions,
        ...getMockTransactions(userData.accounts || [], { existingTransactions: realTransactions }),
      ].sort((a, b) => new Date(b.date) - new Date(a.date))
    : realTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
  return allTransactions;
}

export async function getAccountBalances(uid) {
  const accountsDoc = await getDoc(doc(db, 'users', uid));
  if (!accountsDoc.exists()) {
    throw new Error('User accounts not found');
  }
  return accountsDoc.data().accounts || [];
}

// Real-time updates for account balances
export function subscribeToAccountUpdates(uid, callback) {
  const userDoc = doc(db, 'users', uid);
  return onSnapshot(userDoc, (doc) => {
    if (doc.exists()) {
      callback(doc.data().accounts || []);
    }
  });
}

// Real-time updates for transactions
export function subscribeToTransactions(userId, callback) {
  const transactionsRef = collection(db, 'transactions');
  const q = query(
    transactionsRef,
    where('userId', '==', userId),
    orderBy('date', 'desc')
  );

  const unsubscribe = onSnapshot(q, 
    (querySnapshot) => {
      const realTransactions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        realTransactions.push({
          id: doc.id,
          ...data,
          date: data.date?.toDate() || new Date()
        });
      });
      callback(realTransactions);
    },
    (error) => {
      console.error('Transaction subscription error:', error);
    }
  );

  return unsubscribe;
}

// US: Generate 10-digit bank account number (demo)
const generateBankAccountNumber = () => {
  return Math.floor(1000000000 + Math.random() * 9000000000).toString();
};

// Claireoaks Credit Union demo routing (valid ABA checksum - Chase-style example)
const DEFAULT_ROUTING_NUMBER = '021000021';

// Update existing user's account numbers if they have asterisks
export const updateAccountNumbers = async (uid) => {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);
  
  if (userDoc.exists()) {
    const userData = userDoc.data();
    let needsUpdate = false;
    
    const updatedAccounts = userData.accounts.map(account => {
      // Check if account number has asterisks or needs to be updated
      if (!account.accountNumber || account.accountNumber.includes('*')) {
        needsUpdate = true;
        return {
          ...account,
          accountNumber: generateBankAccountNumber()
        };
      }
      return account;
    });

    if (needsUpdate) {
      await updateDoc(userRef, {
        accounts: updatedAccounts
      });
    }

    return updatedAccounts;
  }
};

export async function setupInitialUserData(uid, email) {
  const userRef = doc(db, 'users', uid);
  
  // Check if user document already exists
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) {
    // Create initial user data (US retail banking)
    await setDoc(userRef, {
      name: email.split('@')[0],
      email: email,
      role: 'user',
      accounts: [
        {
          type: 'Checking',
          accountNumber: generateBankAccountNumber(),
          balance: 36750.60,
          available: 2500.00,
          routingNumber: DEFAULT_ROUTING_NUMBER
        },
        {
          type: 'Savings',
          accountNumber: generateBankAccountNumber(),
          balance: 10230.40,
          available: 10000.00,
          routingNumber: DEFAULT_ROUTING_NUMBER
        }
      ]
    });
    
  } else {
    // If user exists, ensure account numbers are properly formatted
    await updateAccountNumbers(uid);
  }
}

export async function processTransfer(transferData) {
  const { 
    fromAccount, 
    toAccount, 
    amount, 
    reference, 
    transferType,
    userId,
    recipientEmail,
    accountName,
    toBank
  } = transferData;

  try {
    await runTransaction(db, async (transaction) => {
      // Get source user document
      const userRef = doc(db, 'users', userId);
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const accounts = [...userData.accounts];
      const sourceAccountIndex = accounts.findIndex(acc => acc.type === fromAccount);

      if (sourceAccountIndex === -1) {
        throw new Error('Source account not found');
      }

      if (isAccountDisabled(accounts[sourceAccountIndex])) {
        throw new Error('This account is disabled. Choose another account.');
      }

      const transferAmount = Number(amount);
      if (!Number.isFinite(transferAmount) || transferAmount <= 0) {
        throw new Error('Enter a valid transfer amount.');
      }

      // Spendable funds are `available` (what the UI shows). Fall back to balance if available is missing.
      const source = accounts[sourceAccountIndex];
      const spendable = Number(
        source.available != null ? source.available : source.balance != null ? source.balance : 0
      );
      if (spendable < transferAmount) {
        throw new Error('Insufficient funds');
      }

      // Update source account
      const nextAvailable = Math.max(0, spendable - transferAmount);
      const ledger = Number(source.balance != null ? source.balance : spendable);
      accounts[sourceAccountIndex] = {
        ...source,
        available: nextAvailable,
        balance: Math.max(0, ledger - transferAmount),
      };

      // Handle different transfer types
      let description = '';
      let recipientDescription = '';
      let recipientDocs = null;

      switch (transferType) {
        case 'internal':
          // Internal transfer between own accounts
          const destAccountIndex = accounts.findIndex(acc => acc.type === toAccount);
          if (destAccountIndex === -1) {
            throw new Error('Destination account not found');
          }
          if (isAccountDisabled(accounts[destAccountIndex])) {
            throw new Error('Destination account is disabled. Choose another account.');
          }
          {
            const dest = accounts[destAccountIndex];
            const destAvailable = Number(dest.available != null ? dest.available : dest.balance || 0);
            const destBalance = Number(dest.balance != null ? dest.balance : destAvailable);
            accounts[destAccountIndex] = {
              ...dest,
              available: destAvailable + transferAmount,
              balance: destBalance + transferAmount,
            };
          }
          description = `Transfer to ${toAccount}: ${reference}`;
          recipientDescription = `Transfer from ${fromAccount}: ${reference}`;
          break;

        case 'user':
          // Transfer to another Claireoaks member (same institution)
          const recipientQuery = query(
            collection(db, 'users'),
            where('email', '==', recipientEmail)
          );
          recipientDocs = await getDocs(recipientQuery);
          
          if (recipientDocs.empty) {
            throw new Error('Recipient not found');
          }

          const recipientRef = doc(db, 'users', recipientDocs.docs[0].id);
          const recipientData = recipientDocs.docs[0].data();
          
          // Update recipient's default account (checking)
          const recipientAccounts = [...recipientData.accounts];
          const recipientAccountIndex = recipientAccounts.findIndex(acc => acc.type === 'Current' || acc.type === 'Checking');
          
          if (recipientAccountIndex === -1) {
            throw new Error('Recipient account not found');
          }

          recipientAccounts[recipientAccountIndex].balance =
            Number(recipientAccounts[recipientAccountIndex].balance || 0) + transferAmount;
          recipientAccounts[recipientAccountIndex].available =
            Number(recipientAccounts[recipientAccountIndex].available || 0) + transferAmount;

          transaction.update(recipientRef, { accounts: recipientAccounts });
          
          description = `Transfer to ${recipientEmail}: ${reference}`;
          recipientDescription = `Transfer from ${userData.email}: ${reference}`;
          break;

        case 'external':
          // US ACH / external bank transfer (demo)
          description = `Transfer to ${toBank} (${accountName}): ${reference}`;
          break;

        case 'international':
          // International transfer (debit only)
          description = transferData.description || `International transfer: ${reference}`;
          break;

        default:
          throw new Error('Invalid transfer type');
      }

      // Update source user's accounts
      transaction.update(userRef, { accounts });

      // Create transaction records
      const transactionsRef = collection(db, 'transactions');
      
      // Debit transaction for source account
      await addDoc(transactionsRef, {
        userId,
        accountType: fromAccount,
        type: 'debit',
        amount: transferAmount,
        description,
        date: serverTimestamp(),
        category: 'Transfer',
        status: 'Completed'
      });

      // Credit transaction for recipient (if internal or user transfer)
      if (transferType !== 'external' && transferType !== 'international') {
        const recipientUserId = transferType === 'internal' ? userId : recipientDocs.docs[0].id;
        const recipientCreditAccountType =
          transferType === 'internal'
            ? toAccount
            : (recipientDocs.docs[0].data().accounts || []).find(
                (a) => a.type === 'Checking' || a.type === 'Current'
              )?.type || 'Checking';
        await addDoc(transactionsRef, {
          userId: recipientUserId,
          accountType: recipientCreditAccountType,
          type: 'credit',
          amount: transferAmount,
          description: recipientDescription,
          date: serverTimestamp(),
          category: 'Transfer',
          status: 'Completed'
        });
      }
    });

    return true;
  } catch (error) {
    console.error('Transfer error:', error);
    throw error;
  }
}

export async function processBillPayment(paymentData) {
  const {
    fromAccount,
    provider,
    accountNumber,
    amount,
    scheduleDate,
    memo,
    userId
  } = paymentData;

  try {
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const accounts = [...userData.accounts];
      const sourceAccountIndex = accounts.findIndex(acc => acc.type === fromAccount);

      if (sourceAccountIndex === -1) {
        throw new Error('Source account not found');
      }

      // Create bill payment record
      const billPaymentsRef = collection(db, 'billPayments');
      await addDoc(billPaymentsRef, {
        userId,
        fromAccount,
        provider,
        accountNumber,
        amount,
        scheduleDate,
        memo,
        status: 'Scheduled',
        createdAt: serverTimestamp()
      });

      // Create pending transaction
      const transactionsRef = collection(db, 'transactions');
      await addDoc(transactionsRef, {
        userId,
        accountType: fromAccount,
        type: 'debit',
        amount,
        description: `Bill Payment - ${provider}`,
        date: serverTimestamp(),
        category: 'Bill Pay',
        status: 'Pending',
        scheduledDate: scheduleDate
      });
    });

    return true;
  } catch (error) {
    console.error('Bill payment error:', error);
    throw error;
  }
}

export async function processMobileDeposit(depositData) {
  const {
    toAccount,
    amount,
    frontImage,
    backImage,
    userId
  } = depositData;

  try {
    // Create deposit record
    const depositsRef = collection(db, 'mobileDeposits');
    const depositRef = await addDoc(depositsRef, {
      userId,
      toAccount,
      amount,
      status: 'Pending Review',
      createdAt: serverTimestamp(),
      reviewedAt: null,
      imageUrls: {
        front: null,
        back: null
      }
    });

    // Upload images to storage (implementation depends on your storage setup)
    // const frontImageUrl = await uploadImage(frontImage, `deposits/${depositRef.id}/front`);
    // const backImageUrl = await uploadImage(backImage, `deposits/${depositRef.id}/back`);

    // Create pending transaction
    const transactionsRef = collection(db, 'transactions');
    await addDoc(transactionsRef, {
      userId,
      accountType: toAccount,
      type: 'credit',
      amount,
      description: 'Mobile Deposit',
      date: serverTimestamp(),
      category: 'Deposit',
      status: 'Pending',
      depositId: depositRef.id
    });

    return true;
  } catch (error) {
    console.error('Mobile deposit error:', error);
    throw error;
  }
}

function normalizeAccountType(type) {
  return String(type || '').trim();
}

function buildAccountFromInput(account, index = 0) {
  const type = normalizeAccountType(account?.type) || 'Checking';
  const slug = type.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'account';
  return {
    type,
    balance: Number(account?.balance || 0),
    available: Number(account?.available || 0),
    accountNumber: account?.accountNumber
      ? String(account.accountNumber).replace(/\D/g, '').slice(0, 17)
      : generateBankAccountNumber(),
    routingNumber: account?.routingNumber
      ? String(account.routingNumber).replace(/\D/g, '').slice(0, 9)
      : DEFAULT_ROUTING_NUMBER,
    id: `${slug}-${Date.now()}-${index}`,
    createdAt: new Date().toISOString(),
  };
}

// Update createNewUserWithAccounts to use the new generator
export const createNewUserWithAccounts = async ({ email, password, name, phone, address, role, accounts }) => {
  try {
    const adminAuth = auth.currentUser;
    const newUserCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = newUserCredential.user.uid;
    await auth.updateCurrentUser(adminAuth);

    const sourceAccounts = Array.isArray(accounts) && accounts.length > 0
      ? accounts
      : [
          { type: 'Checking', balance: 0, available: 0 },
          { type: 'Savings', balance: 0, available: 0 },
        ];

    let nextAccounts = sourceAccounts.map((account, index) => buildAccountFromInput(account, index));

    // Business rule: Inheritance replaces Savings
    const hasInheritance = nextAccounts.some((a) => String(a.type).toLowerCase() === 'inheritance');
    if (hasInheritance) {
      nextAccounts = nextAccounts.filter((a) => String(a.type).toLowerCase() !== 'savings');
    }

    await setDoc(doc(db, 'users', userId), {
      email,
      name: name || email.split('@')[0],
      phone,
      address: {
        street: address.street || '',
        city: address.city || '',
        county: address.county || '',
        postCode: address.postCode || address.zipCode || ''
      },
      role: role || 'user',
      status: 'active',
      createdAt: serverTimestamp(),
      createdBy: adminAuth.email,
      accounts: nextAccounts
    });

    const investmentAccount = getInvestmentAccount(nextAccounts);
    if (investmentAccount) {
      await syncInvestmentReturnTransactions(
        userId,
        investmentAccount.available ?? investmentAccount.balance ?? 0
      );
    }

    return userId;
  } catch (error) {
    console.error('Error creating new user:', error);
    throw error;
  }
};

/** Add a new account to an existing user (admin). */
export const addUserAccount = async (userId, accountInput = {}) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) {
    throw new Error('User not found');
  }

  const userData = userDoc.data();
  let accounts = Array.isArray(userData.accounts) ? [...userData.accounts] : [];
  const next = buildAccountFromInput(accountInput, accounts.length);

  const duplicate = accounts.some(
    (a) => String(a?.type || '').toLowerCase() === String(next.type).toLowerCase()
  );
  if (duplicate) {
    throw new Error(`An account with type "${next.type}" already exists for this user.`);
  }

  accounts.push(next);

  const hasInheritance = accounts.some((a) => String(a?.type || '').toLowerCase() === 'inheritance');
  if (hasInheritance) {
    accounts = accounts.filter((a) => String(a?.type || '').toLowerCase() !== 'savings');
  }

  await updateDoc(userRef, {
    accounts,
    lastModified: serverTimestamp(),
  });

  const investmentAccount = getInvestmentAccount(accounts);
  if (investmentAccount) {
    await syncInvestmentReturnTransactions(
      userId,
      investmentAccount.available ?? investmentAccount.balance ?? 0
    );
  }

  return accounts;
};

// Initialize accounts for existing users if needed
export const initializeUserAccounts = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      if (!userData.accounts || userData.accounts.length === 0) {
        // Set up default accounts
        const defaultAccounts = [
          {
            type: 'Checking',
            balance: 0,
            available: 0,
            accountNumber: generateBankAccountNumber(),
            routingNumber: DEFAULT_ROUTING_NUMBER,
            id: 'current-' + Date.now(),
            createdAt: new Date().toISOString()
          },
          {
            type: 'Savings',
            balance: 0,
            available: 0,
            accountNumber: generateBankAccountNumber(),
            routingNumber: DEFAULT_ROUTING_NUMBER,
            id: 'savings-' + Date.now(),
            createdAt: new Date().toISOString()
          }
        ];

        await updateDoc(userRef, {
          accounts: defaultAccounts,
          updatedAt: serverTimestamp()
        });

      }
    }
  } catch (error) {
    console.error('Error initializing accounts:', error);
    throw error;
  }
};

// Get all users with their account balances
export const getAllUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = [];
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const accounts = userData.accounts || [];

      // Process accounts with proper number conversion
      const processedAccounts = accounts.map((account, index) => ({
        id: index.toString(),
        type: account.type,
        balance: Number(account.balance || 0),
        available: Number(account.available || 0),
        accountNumber: account.accountNumber,
        routingNumber: String(account.routingNumber || account.sortCode || DEFAULT_ROUTING_NUMBER).replace(
          /\D/g,
          ''
        ),
        disabled: account.disabled === true,
      }));

      // Ensure all user data fields are properly structured
      const personalInfo = userData.personalInfo || {};
      const contactDetails = userData.contactDetails || {};
      const documents = userData.documents || {};

      users.push({
        uid: userDoc.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        status: userData.status,
        transferLimit: userData.transferLimit || null,
        accounts: processedAccounts,
        personalInfo: {
          firstName: personalInfo.firstName || '',
          lastName: personalInfo.lastName || '',
          dateOfBirth: personalInfo.dateOfBirth || null,
          nationality: personalInfo.nationality || '',
          maritalStatus: personalInfo.maritalStatus || '',
          address: personalInfo.address || {
            street: '',
            city: '',
            county: '',
            postCode: '',
            country: ''
          }
        },
        contactDetails: {
          phoneNumber: contactDetails.phoneNumber || '',
          alternativeEmail: contactDetails.alternativeEmail || '',
          emergencyContact: contactDetails.emergencyContact || {
            name: '',
            relationship: '',
            phone: ''
          }
        },
        documents: {
          idCardType: documents.idCardType || '',
          idCardNumber: documents.idCardNumber || '',
          verificationStatus: documents.verificationStatus || 'pending',
          infoRequest: documents.infoRequest || null,
          infoRequestedAt: documents.infoRequestedAt || null
        },
        registrationDate: userData.registrationDate || null,
        createdAt: userData.createdAt || null,
        showMockTransactions: userData.showMockTransactions === true
      });
    }

    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

/** Set whether this user sees mock/sample transactions on their dashboard. */
export async function updateUserShowMockTransactions(userId, showMock) {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, { showMockTransactions: !!showMock });

  // When samples are enabled, ensure Investment accounts have return history
  if (showMock) {
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const accounts = userDoc.data().accounts || [];
      const investmentAccount = getInvestmentAccount(accounts);
      if (investmentAccount) {
        await syncInvestmentReturnTransactions(
          userId,
          investmentAccount.available ?? investmentAccount.balance ?? 0
        );
      }
    }
  }
}

export async function updateUserName(userId, name) {
  const userRef = doc(db, 'users', userId);
  const next = String(name || '').trim();
  if (!next) throw new Error('Name cannot be empty.');
  await updateDoc(userRef, { name: next });
  return next;
}

/**
 * Per-user transfer limit override.
 *
 * If `overrideEnabled` is false, the override is removed and global settings apply.
 * Stored on `users/{uid}.transferLimit` as `{ isEnabled, transactionLimit }`.
 */
export async function updateUserTransferLimit(userId, { overrideEnabled, isEnabled, transactionLimit }) {
  const userRef = doc(db, 'users', userId);
  if (!overrideEnabled) {
    await updateDoc(userRef, { transferLimit: deleteField() });
    return;
  }

  const next = {
    isEnabled: isEnabled !== false,
    transactionLimit: Math.max(0, Number(transactionLimit) || 0),
  };
  await updateDoc(userRef, { transferLimit: next });
}

/** Enable or disable a user account type (admin). Disabled accounts stay in data but are hidden from the member dashboard. */
export const setUserAccountDisabled = async (userId, accountIndex, disabled) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) {
    throw new Error('User not found');
  }

  const userData = userDoc.data();
  const accounts = Array.isArray(userData.accounts) ? [...userData.accounts] : [];
  const idx = parseInt(accountIndex, 10);
  if (isNaN(idx) || idx < 0 || idx >= accounts.length || !accounts[idx]) {
    throw new Error('Account not found');
  }

  if (disabled) {
    const activeCount = accounts.filter((a, i) => i !== idx && !isAccountDisabled(a)).length;
    if (activeCount < 1) {
      throw new Error('Keep at least one active account. You cannot disable every account.');
    }
  }

  accounts[idx] = {
    ...accounts[idx],
    disabled: disabled === true,
  };

  await updateDoc(userRef, {
    accounts,
    lastModified: serverTimestamp(),
  });

  const investmentAccount = getInvestmentAccount(accounts);
  if (investmentAccount) {
    await syncInvestmentReturnTransactions(
      userId,
      investmentAccount.available ?? investmentAccount.balance ?? 0
    );
  } else {
    await deleteAutoGeneratedInvestmentTransactions(userId);
  }

  return accounts.map((account, index) => ({
    ...account,
    id: index.toString(),
    disabled: account.disabled === true,
  }));
};

// Update user account without using serverTimestamp in array
export const updateUserAccount = async (userId, accountIndex, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    let accounts = [...userData.accounts];

    const idx = parseInt(accountIndex, 10);
    if (isNaN(idx) || idx < 0 || idx >= accounts.length || !accounts[idx]) {
      throw new Error('Account not found');
    }

    const existing = accounts[idx];
    const accountNumber =
      updates.accountNumber != null
        ? String(updates.accountNumber).replace(/\D/g, '').slice(0, 17)
        : existing.accountNumber;
    const routingInput =
      updates.routingNumber ?? updates.sortCode ?? existing.routingNumber ?? existing.sortCode ?? '';
    const routingNumber = String(routingInput).replace(/\D/g, '').slice(0, 9);

    const { sortCode: _removed, ...existingRest } = existing;
    const nextType = updates.type ? String(updates.type).trim() : existing.type;
    if (
      nextType &&
      String(nextType).toLowerCase() !== String(existing.type || '').toLowerCase() &&
      accounts.some(
        (a, i) =>
          i !== idx && String(a?.type || '').toLowerCase() === String(nextType).toLowerCase()
      )
    ) {
      throw new Error(`An account with type "${nextType}" already exists for this user.`);
    }

    accounts[idx] = {
      ...existingRest,
      type: nextType,
      balance: Number(updates.balance),
      available: Number(updates.available),
      accountNumber: accountNumber || existing.accountNumber,
      routingNumber: routingNumber || String(existing.routingNumber || existing.sortCode || '').replace(/\D/g, ''),
      disabled: existing.disabled === true,
    };

    // Business rule: if a user has an Inheritance account, they should not have a Savings account.
    // Enforce it here so it applies consistently across admin UI and any other writers.
    const hasInheritance = accounts.some((a) => String(a?.type || '').toLowerCase() === 'inheritance');
    if (hasInheritance) {
      accounts = accounts.filter((a) => String(a?.type || '').toLowerCase() !== 'savings');
    }

    // Update the entire accounts array and add lastModified at root level
    await updateDoc(userRef, {
      accounts: accounts,
      lastModified: serverTimestamp() // Move timestamp to root level
    });

    const investmentAccount = getInvestmentAccount(accounts);
    if (investmentAccount) {
      await syncInvestmentReturnTransactions(
        userId,
        investmentAccount.available ?? investmentAccount.balance ?? 0
      );
    } else {
      await deleteAutoGeneratedInvestmentTransactions(userId);
    }

    return true;
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

// Update transaction (for admin)
export const updateTransaction = async (userId, transactionId, updates) => {
  try {
    const transactionRef = doc(db, 'users', userId, 'transactions', transactionId);
    
    if (updates.deleted) {
      // Soft delete - mark as deleted instead of actually deleting
      await updateDoc(transactionRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: 'admin'
      });
    } else {
      await updateDoc(transactionRef, {
        ...updates,
        lastModified: serverTimestamp(),
        modifiedBy: 'admin'
      });
    }
  } catch (error) {
    console.error('Error updating transaction:', error);
    throw error;
  }
};

// Delete transaction (for admin)
export const deleteTransaction = async (userId, transactionId) => {
  try {
    const transactionRef = doc(db, 'users', userId, 'transactions', transactionId);
    await updateDoc(transactionRef, {
      deleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: 'admin'
    });
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw error;
  }
};

// Function to promote user to admin
export const promoteToAdmin = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'admin',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error promoting user to admin:', error);
    throw error;
  }
};

// Subscribe to real-time updates
export const subscribeToUserUpdates = (callback) => {
  return onSnapshot(collection(db, 'users'), (snapshot) => {
    try {
      const users = snapshot.docs.map(doc => {
        const userData = doc.data();
        const accounts = userData.accounts || [];

        const processedAccounts = accounts.map((account, index) => ({
          id: index.toString(),
          type: account.type,
          balance: Number(account.balance || 0),
          available: Number(account.available || 0),
          accountNumber: account.accountNumber,
          routingNumber: String(account.routingNumber || account.sortCode || DEFAULT_ROUTING_NUMBER).replace(
            /\D/g,
            ''
          ),
          disabled: account.disabled === true,
        }));

        // Ensure all user data fields are properly structured
        const personalInfo = userData.personalInfo || {};
        const contactDetails = userData.contactDetails || {};
        const documents = userData.documents || {};

        return {
          uid: doc.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          status: userData.status,
          transferLimit: userData.transferLimit || null,
          accounts: processedAccounts,
          personalInfo: {
            firstName: personalInfo.firstName || '',
            lastName: personalInfo.lastName || '',
            dateOfBirth: personalInfo.dateOfBirth || null,
            nationality: personalInfo.nationality || '',
            maritalStatus: personalInfo.maritalStatus || '',
            address: personalInfo.address || {
              street: '',
              city: '',
              county: '',
              postCode: '',
              country: ''
            }
          },
          contactDetails: {
            phoneNumber: contactDetails.phoneNumber || '',
            alternativeEmail: contactDetails.alternativeEmail || '',
            emergencyContact: contactDetails.emergencyContact || {
              name: '',
              relationship: '',
              phone: ''
            }
          },
          documents: {
            idCardType: documents.idCardType || '',
            idCardNumber: documents.idCardNumber || '',
            verificationStatus: documents.verificationStatus || 'pending',
            infoRequest: documents.infoRequest || null,
            infoRequestedAt: documents.infoRequestedAt || null
          },
          registrationDate: userData.registrationDate || null,
          createdAt: userData.createdAt || null,
          showMockTransactions: userData.showMockTransactions === true
        };
      });

      callback(users);
    } catch (error) {
      console.error('Error in subscription:', error);
    }
  });
};

export const rejectUserRegistration = async (userId, reason) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status: 'rejected',
      role: 'pending',
      rejectionReason: reason,
      rejectedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Move user to rejected applications collection
    const rejectedRef = doc(db, 'rejectedApplications', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();

    await setDoc(rejectedRef, {
      ...userData,
      rejectionReason: reason,
      rejectedAt: new Date().toISOString()
    });

    return true;
  } catch (error) {
    console.error('Error rejecting registration:', error);
    throw error;
  }
};

export const requestAdditionalInfo = async (userId, requestDetails) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'documents.verificationStatus': 'info_requested',
      'documents.infoRequest': requestDetails,
      'documents.infoRequestedAt': new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Create an info request record
    const requestRef = doc(collection(db, 'infoRequests'));
    await setDoc(requestRef, {
      userId,
      requestDetails,
      status: 'pending',
      createdAt: new Date().toISOString()
    });

    return true;
  } catch (error) {
    console.error('Error requesting additional information:', error);
    throw error;
  }
};

// Update the existing updateUserStatus function to handle verification
export const updateUserStatus = async (userId, newStatus) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();

    // Update user status
    await updateDoc(userRef, {
      status: newStatus,
      role: newStatus === 'approved' ? 'user' : 'pending',
      updatedAt: new Date().toISOString()
    });

    // If approving user, create default accounts if they don't exist
    if (newStatus === 'approved' && (!userData.accounts || userData.accounts.length === 0)) {
      const accounts = [
        {
          type: 'Checking',
          balance: 0,
          available: 0,
          accountNumber: generateBankAccountNumber(),
          routingNumber: DEFAULT_ROUTING_NUMBER,
          id: 'current-' + Date.now(),
          createdAt: new Date().toISOString()
        },
        {
          type: 'Savings',
          balance: 0,
          available: 0,
          accountNumber: generateBankAccountNumber(),
          routingNumber: DEFAULT_ROUTING_NUMBER,
          id: 'savings-' + Date.now(),
          createdAt: new Date().toISOString()
        }
      ];

      await updateDoc(userRef, {
        accounts: accounts
      });

      // Move to approved users collection
      const approvedRef = doc(db, 'approvedUsers', userId);
      await setDoc(approvedRef, {
        ...userData,
        accounts,
        approvedAt: new Date().toISOString()
      });
    }

    return true;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

// Add new transaction (for admin)
export const addAdminTransaction = async (userId, transactionData) => {
  try {
    const transactionsRef = collection(db, 'transactions');
    
    // Format the transaction data
    const newTransaction = {
      userId,
      accountType: transactionData.accountType,
      type: transactionData.type,
      amount: Number(transactionData.amount),
      description: transactionData.description,
      date: transactionData.date ? new Date(transactionData.date) : serverTimestamp(),
      category: transactionData.category,
      status: transactionData.status || 'Completed',
      createdAt: serverTimestamp(),
      createdBy: 'admin',
      reference: transactionData.reference || ''
    };

    // Add the transaction
    const docRef = await addDoc(transactionsRef, newTransaction);

    // If this is a real transaction (not just for display), update account balance
    if (transactionData.updateBalance) {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const accounts = [...userData.accounts];
        const accountIndex = accounts.findIndex(acc => acc.type === transactionData.accountType);

        if (accountIndex !== -1) {
          const multiplier = transactionData.type === 'credit' ? 1 : -1;
          accounts[accountIndex].balance += (multiplier * Number(transactionData.amount));
          accounts[accountIndex].available += (multiplier * Number(transactionData.amount));

          await updateDoc(userRef, {
            accounts,
            lastModified: serverTimestamp()
          });
        }
      }
    }

    return docRef.id;
  } catch (error) {
    console.error('Error adding admin transaction:', error);
    throw error;
  }
};

// Delete all transactions for a user
export const deleteAllUserTransactions = async (userId) => {
  try {
    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    return querySnapshot.size;
  } catch (error) {
    console.error('Error deleting transactions:', error);
    throw error;
  }
};

// Find a user by email
export const findUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('email', '==', email),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error finding user:', error);
    throw error;
  }
};

// Delete specific transaction and update balance
export const deleteSpecificTransaction = async (userId, transactionId) => {
  try {
    const transactionRef = doc(db, 'transactions', transactionId);
    const transactionDoc = await getDoc(transactionRef);
    
    if (!transactionDoc.exists()) {
      throw new Error('Transaction not found');
    }

    const transactionData = transactionDoc.data();
    
    // Update account balance before deleting the transaction
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const accounts = [...userData.accounts];
      const accountIndex = accounts.findIndex(acc => acc.type === transactionData.accountType);

      if (accountIndex !== -1) {
        // Reverse the original transaction's effect on the balance
        const multiplier = transactionData.type === 'credit' ? -1 : 1;
        accounts[accountIndex].balance += (multiplier * Number(transactionData.amount));
        accounts[accountIndex].available += (multiplier * Number(transactionData.amount));

        // Update user's account balances
        await updateDoc(userRef, {
          accounts,
          lastModified: serverTimestamp()
        });
      }
    }

    // Delete the transaction
    await deleteDoc(transactionRef);
    
    return {
      success: true,
      message: 'Transaction deleted and balance updated successfully'
    };
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw error;
  }
}; 

async function deleteQueryInBatches(q) {
  const snapshot = await getDocs(q);
  if (snapshot.empty) return 0;

  const docs = snapshot.docs;
  const CHUNK_SIZE = 450;
  let deleted = 0;

  for (let i = 0; i < docs.length; i += CHUNK_SIZE) {
    const chunk = docs.slice(i, i + CHUNK_SIZE);
    const batch = writeBatch(db);
    chunk.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    deleted += chunk.length;
  }

  return deleted;
}

/**
 * Delete a user's Firestore data.
 *
 * Note: Client-side code cannot delete a Firebase Auth user without an Admin SDK backend.
 * This removes the user's `users/{uid}` document and related app data.
 */
export async function deleteUserData(userId) {
  if (!userId) throw new Error('Missing userId');

  // Delete related collection docs
  const transactionsDeleted = await deleteQueryInBatches(
    query(collection(db, 'transactions'), where('userId', '==', userId))
  );
  const infoRequestsDeleted = await deleteQueryInBatches(
    query(collection(db, 'infoRequests'), where('userId', '==', userId))
  );

  // Delete any derived/aux docs keyed by uid (ignore if missing)
  await Promise.allSettled([
    deleteDoc(doc(db, 'approvedUsers', userId)),
    deleteDoc(doc(db, 'rejectedApplications', userId)),
  ]);

  // Finally delete primary user profile
  await deleteDoc(doc(db, 'users', userId));

  return { transactionsDeleted, infoRequestsDeleted };
}